<script setup>

import {defineProps, inject, onMounted, ref, watch} from "vue";
import SchoolService from "@/services/SchoolService";
import SchoolAdminService from "@/services/SchoolAdminService";
import AccountService from "@/services/AccountService";
import {useStore} from "vuex";
import {useRouter} from "vue-router";
import ActivityLog from "@/components/ActivityLog.vue";

//general
const store = useStore();
const router = useRouter();
const props = defineProps({
  showSchoolsTab: Boolean,
});

 
const showToastMessage = inject("showToastMessage");

// School admin notification assignments
const availableAdmins = ref([]);
const loadingAdminList = ref(false);
const isSuperAdmin = ref(false);

onMounted(() => {
  listSchoolDetail();
  loadAdminList();
  checkUserRole();
});

const checkUserRole = async () => {
  const response = await AccountService.isSuperAdmin(router, store);
  if (response.status === 'success') {
    isSuperAdmin.value = response.isSuperAdmin;
  }
};

// Pagination and search state
const currentPage = ref(1);
const perPage = ref(10);
const searchKeyword = ref('');
const sortArray = ref([]);

const listSchoolDetail = (page = null, rows = null, search = null, sort = null) => {
  table_loading.value = true;

  // Use provided parameters or current state
  const requestPage = page !== null ? page : currentPage.value;
  const requestPerPage = rows !== null ? rows : perPage.value;
  const requestSearch = search !== null ? search : searchKeyword.value;
  const requestSort = sort !== null ? sort : sortArray.value;

  SchoolService.searchSchools(router, store, requestPage, requestPerPage, requestSearch, requestSort).then((response) => {
    if(response.status === 'error') {
      console.error(response.message);
      table_loading.value = false;
      return;
    }

    schools.value = response.schools.map(school => ({
      ...school,
      id: school.id,
      name: school.name,
      firstAlias: school.aliases?.[0]?.alias || '',
      aliasCount: school.aliases?.length || 0,
      firstReportingStream: school.reporting_streams?.[0]?.name || '',
      reportingStreamCount: school.reporting_streams?.length || 0,
      aliasList: school.aliases?.map(alias => alias.alias).join(', ') || '',
      reportingStreamList: school.reporting_streams?.map(stream => stream.name).join(', ') || '',
      contractCount: school.contracts?.length || 0,
      contractNumber: school.contracts?.[0]?.contract_number || '',
      contractTermStartDate: school.contracts?.[0]?.term_start_date || '',
      contractTermStartDateSort: school.contracts?.[0]?.term_start_date || '1900-01-01',
      contractTermEndDate: school.contracts?.[0]?.term_end_date || '',
      // Blank end date = open-ended: sort as ending furthest in the future.
      contractTermEndDateSort: school.contracts?.[0]?.term_end_date || '9999-12-31',
      activeUsersCount: school.active_users_count || 0,
      // Use admins from API response instead of making separate calls
      notifyAdmins: school.admins?.map(admin => admin.id) || [],
      notifyAdminsList: school.admins?.map(admin => admin.email).join(', ') || '',
      loadingNotifyAdmins: false,
    }));

    // Update pagination state
    if (response.pagination) {
      currentPage.value = response.pagination.current_page;
      perPage.value = response.pagination.per_page;
      totalRecords.value = response.pagination.total;
    }

    table_loading.value = false;
  }).catch((error) => {
    console.error(error);
    table_loading.value = false;
  });
};

// Handle page change from DataTable
const onPageChange = (event) => {
  currentPage.value = event.page + 1; // PrimeVue uses 0-based index
  perPage.value = event.rows;
  listSchoolDetail(currentPage.value, perPage.value);
};

// Handle sort change from DataTable
const onSort = (event) => {
  // Convert PrimeVue sort event to our API format
  const sortFields = [];
  if (event.sortField) {
    sortFields.push({
      field: event.sortField,
      direction: event.sortOrder === 1 ? 'asc' : 'desc'
    });
  }
  sortArray.value = sortFields;
  currentPage.value = 1; // Reset to first page on sort
  listSchoolDetail(1, perPage.value, searchKeyword.value, sortFields);
};

// Load available admins for MultiSelect dropdown
const loadAdminList = async () => {
  loadingAdminList.value = true;
  const response = await SchoolAdminService.getAdminList(router, store);
  if (response.status === 'success') {
    availableAdmins.value = response.admins;
  } else {
    console.error('Failed to load admin list:', response.message);
  }
  loadingAdminList.value = false;
};

// Update assigned admins for a school
const updateSchoolAdmins = async (school, adminIds) => {

  // Prevent concurrent updates
  if (school.loadingNotifyAdmins) {
    return;
  }

  // Store the previous state in case we need to revert
  const previousAdmins = Array.isArray(school.notifyAdmins) ? [...school.notifyAdmins] : [];

  // No-op if values are unchanged (order-insensitive)
  const sameSet = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const sa = new Set(a);
    for (const x of b) if (!sa.has(x)) return false;
    return true;
  };

  const areEqual = sameSet(previousAdmins, adminIds || []);

  if (areEqual) {
    return;
  }

  school.loadingNotifyAdmins = true;

  try {
    const response = await SchoolAdminService.updateSchoolAdmins(router, store, school.id, adminIds);

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to update notify admins');
    }
    // Success: Update the school's notifyAdmins
    school.notifyAdmins = adminIds || [];
  } catch (err) {
    console.error('Error updating admins:', err);
    showToastMessage?.({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Unexpected error updating notify admins',
      life: 5000,
    });
    // Revert to previous state on error
    school.notifyAdmins = previousAdmins;
  } finally {
    school.loadingNotifyAdmins = false;
  }
};

// general data table
const schools = ref([]);
const filters = ref({
  global: {value: null},
  name: {value: null},
  alias: {value: null},
  contract_name: {value: null}
});

// Handle search from global filter with debounce
let searchTimeout = null;
watch(() => filters.value.global.value, (newValue) => {
  // Clear existing timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  // Set new timeout for debounced search (wait 500ms after user stops typing)
  searchTimeout = setTimeout(() => {
    searchKeyword.value = newValue || '';
    currentPage.value = 1; // Reset to first page on search
    listSchoolDetail(1, perPage.value, searchKeyword.value);
  }, 500);
});

const selectedSchool = ref(null);

const table_loading = ref(true);
const totalRecords = ref(0);

// drawer
const newSchoolDrawer = ref(false);
const detailSchoolDrawer = ref(false);

watch(detailSchoolDrawer, (newValue) => {
  if (!newValue) {
    contractMode.value = 'display';
    showNewAliasInput.value = false;
    showNewReportingStreamsInput.value = false;
    newAlias.value = '';
    newReportingStream.value = '';
  }
});

const toggleNewSchoolDrawer = () => {
  newSchoolDrawer.value = !newSchoolDrawer.value;
}

const schoolActivityLogs = ref([]);
const logOffset = ref(0);
const canLoadMoreLogs = ref(true);
const selectSchool = async (school) => {
  detailSchoolDrawer.value = true;
  selectedSchool.value = school;
  logOffset.value = 0;
  canLoadMoreLogs.value = true;

  // Load school activity logs
  const response = await SchoolService.getSchoolActivityLogs(router, store, school.id, logOffset.value);
  if (response.logs.length < 5) {
    canLoadMoreLogs.value = false;
  }
  schoolActivityLogs.value = response.logs;

  // Admins are already loaded from the initial school details API call
  // No need to fetch them again
}



const loadMoreLogs = async () => {
  logOffset.value += 5;
  const response = await SchoolService.getSchoolActivityLogs(router, store, selectedSchool.value.id, logOffset.value);
  schoolActivityLogs.value = schoolActivityLogs.value.concat(response.logs);
  if (response.logs.length < 5) {
    canLoadMoreLogs.value = false;
  }
}



const aliasesCollapsed = ref(false);
const aliasesIcon = ref('pi pi-chevron-down');
const aliasesEditingRows = ref([]);
const aliasEditLoading = ref(false);

const reportingStreamsCollapsed = ref(false);
const reportingStreamsIcon = ref('pi pi-chevron-down');
const reportingStreamsEditingRows = ref([]);
const reportingStreamEditLoading = ref(false);

const contractsCollapsed = ref(false);
const contractsIcon = ref('pi pi-chevron-down');
const contractMode = ref('display');

const logsCollapsed = ref(false);
const logsIcon = ref('pi pi-chevron-down');

const notifyAdminsCollapsed = ref(false);
const notifyAdminsIcon = ref('pi pi-chevron-down');

const selectedContract = ref(null);
const contractModeChange = (mode) => {
  contractMode.value = mode;
  if (mode === 'display') {
    selectedContract.value = null;
  } else {
    selectedContract.value = selectedSchool.value.contracts[0];
  }
};

const addContract = () => {
  contractMode.value = 'edit';
  selectedContract.value = {
    id: -1,
    contract_number: '',
    term_start_date: '',
    term_end_date: '',
  };
};

const saveContractLoading = ref(false);

const saveContract = async () => {
  saveContractLoading.value = true;
  if (selectedContract.value.contract_number === '') {
    showToastMessage('error', 'Error', 'Contract Number is required');
    saveContractLoading.value = false;
    return;
  }

  if (selectedContract.value.term_start_date === '') {
    showToastMessage('error', 'Error', 'Start Date is required');
    saveContractLoading.value = false;
    return;
  }

  const endDateValidationResult = SchoolService.validateStartEndDate(selectedContract.value.term_start_date, selectedContract.value.term_end_date);
  if (endDateValidationResult.status === 'error') {
    showToastMessage('error', 'Error', endDateValidationResult.message);
    saveContractLoading.value = false;
    return;
  }

  try {
    if (selectedContract.value.id > 0) {
      const updateContractResponse = await SchoolService.updateContract(router, store, selectedSchool.value.id, selectedContract.value.id, selectedContract.value.term_start_date, selectedContract.value.term_end_date);
      if (updateContractResponse.status === 'success') {
        showToastMessage('success', 'School Updated', `Contract ${selectedContract.value.contract_number} has been saved for school ${selectedSchool.value.name}`);
        const updatedContract = updateContractResponse.contract;
        selectedSchool.value.contracts[0].term_start_date = new Date(updatedContract.term_start_date).toISOString().split('T')[0];
        selectedSchool.value.contracts[0].term_end_date = updatedContract.term_end_date
            ? new Date(updatedContract.term_end_date).toISOString().split('T')[0]
            : '';
        updateSchoolsTableWithoutReloading(updatedContract, 'contract');
      } else {
        showToastMessage('error', 'Error', updateContractResponse.message);
      }
    } else {
      //before creating contract, validate contract number following our general rule
      const nameRegex = /^[a-zA-Z0-9-_() ]+$/;
      if (!nameRegex.test(selectedContract.value.contract_number)) {
        showToastMessage('error', 'Error', 'Contract number can only contain alphanumeric characters, hyphen, underscore and parentheses');
        return;
      }

      const createContractResponse = await SchoolService.createContract(router, store, selectedSchool.value.id, selectedContract.value.contract_number, selectedContract.value.term_start_date, selectedContract.value.term_end_date);
      if (createContractResponse.status === 'success') {
        showToastMessage('success', 'School Updated', `Contract ${selectedContract.value.contract_number} has been saved for school ${selectedSchool.value.name}`);
        const createdContract = createContractResponse.contract;
        selectedSchool.value.contracts.push({
          id: createdContract.id,
          contract_number: createdContract.contract_number,
          term_start_date: createdContract.term_start_date,
          term_end_date: createdContract.term_end_date,
        });

        //update selectedContract id
        selectedContract.value.id = createdContract.id;

        selectedSchool.value.contractCount = 1;

        updateSchoolsTableWithoutReloading(selectedSchool.value, 'contract');
      } else {
        showToastMessage('error', 'Error', createContractResponse.message);
      }
    }

    contractMode.value = 'display';
    selectedSchool.value.contracts[0] = selectedContract.value;
  } finally {
    saveContractLoading.value = false;
  }
};


const toggleAliasesPanel = () => {
  aliasesCollapsed.value = !aliasesCollapsed.value;
  aliasesIcon.value = aliasesCollapsed.value ? 'pi pi-chevron-right' : 'pi pi-chevron-down';
};

const toggleReportingStreamsPanel = () => {
  reportingStreamsCollapsed.value = !reportingStreamsCollapsed.value;
  reportingStreamsIcon.value = reportingStreamsCollapsed.value ? 'pi pi-chevron-right' : 'pi pi-chevron-down';
};

const toggleContractsPanel = () => {
  contractsCollapsed.value = !contractsCollapsed.value;
  contractsIcon.value = contractsCollapsed.value ? 'pi pi-chevron-right' : 'pi pi-chevron-down';
};

const toggleLogsPanel = () => {
  logsCollapsed.value = !logsCollapsed.value;
  logsIcon.value = logsCollapsed.value ? 'pi pi-chevron-right' : 'pi pi-chevron-down';
};

const toggleNotifyAdminsPanel = () => {
  notifyAdminsCollapsed.value = !notifyAdminsCollapsed.value;
  notifyAdminsIcon.value = notifyAdminsCollapsed.value ? 'pi pi-chevron-right' : 'pi pi-chevron-down';
};

const updateSchoolsTableWithoutReloading = (school, field) => {
  const relatedSchool = schools.value.find(school => school.id === selectedSchool.value.id);

  if (field === 'alias') {
    relatedSchool.firstAlias = selectedSchool.value.aliases[0].alias;
    relatedSchool.aliasCount = selectedSchool.value.aliases.length;
  } else if (field === 'reporting_stream') {
    relatedSchool.firstReportingStream = selectedSchool.value.reporting_streams[0].name;
    relatedSchool.reportingStreamCount = selectedSchool.value.reporting_streams.length;
  } else if (field === 'contract') {

    relatedSchool.contractNumber = selectedSchool.value.contracts[0].contract_number;
    relatedSchool.contractTermStartDate = new Date(selectedSchool.value.contracts[0].term_start_date)
        .toISOString().split('T')[0];
    relatedSchool.contractTermEndDate = selectedSchool.value.contracts[0].term_end_date
        ? new Date(selectedSchool.value.contracts[0].term_end_date).toISOString().split('T')[0]
        : '';
    relatedSchool.contractTermEndDateSort = selectedSchool.value.contracts[0].term_end_date || '9999-12-31';
  }
};

const onRowEditSaveAlias = async (event) => {
  aliasEditLoading.value = true;

  event.newData.alias = event.newData.alias?.trim();

  //validate all names can only contains -_() and alphanumeric
  const nameRegex = /^[a-zA-Z0-9-_() ]+$/;
  if (!nameRegex.test(event.newData.alias)) {
    showToastMessage('error', 'Error', 'Alias can only contain alphanumeric characters, hyphen, underscore and parentheses');
    aliasEditLoading.value = false;
    return;
  }

  let {newData, index} = event;

  const editAliasResponse = await SchoolService.editAlias(router, store, newData.id, newData.alias);
  if (editAliasResponse.status === 'success') {
    showToastMessage('success', 'School Updated', `Alias ${newData.alias} has been saved for school ${selectedSchool.value.name}`);
    selectedSchool.value.aliases[index].alias = newData.alias;

    updateSchoolsTableWithoutReloading(selectedSchool.value, 'alias');
  } else {
    showToastMessage('error', 'Error', editAliasResponse.message);
  }

  aliasesEditingRows.value = [];
  aliasEditLoading.value = false;
};

const onRowEditSaveReportingStream = async (event) => {
  reportingStreamEditLoading.value = true;

  //validate all names can only contains -_() and alphanumeric
  const nameRegex = /^[a-zA-Z0-9-_() ]+$/;
  if (!nameRegex.test(event.newData.name)) {
    showToastMessage('error', 'Error', 'Reporting Stream name can only contain alphanumeric characters, hyphen, underscore and parentheses');
    reportingStreamEditLoading.value = false;
    return;
  }

  event.newData.name = event.newData.name?.trim();

  //check if reporting stream already exists from front data, reporting stream name should be unique inside one school
  if (selectedSchool.value.reporting_streams.find(stream => stream.name === event.newData.name)) {
    showToastMessage('error', 'Error', 'Reporting Stream Name is already taken');
    reportingStreamEditLoading.value = false;
    return;
  }

  let {newData, index} = event;

  const editReportingStreamResponse = await SchoolService.editReportingStream(router, store, newData.id, newData.name);
  if (editReportingStreamResponse.status === 'success') {
    showToastMessage('success', 'School Updated', `Reporting Stream ${newData.name} has been saved for school ${selectedSchool.value.name}`);
    selectedSchool.value.reporting_streams[index].name = newData.name;

    updateSchoolsTableWithoutReloading(selectedSchool.value, 'reporting_stream');
  } else {
    showToastMessage('error', 'Error', 'Failed to save reporting stream');
  }

  reportingStreamsEditingRows.value = [];
  reportingStreamEditLoading.value = false;
};

const showNewAliasInput = ref(false);
const saveAliasLoading = ref(false);
const newAlias = ref('');

const showNewReportingStreamsInput = ref(false);
const saveReportingStreamLoading = ref(false);
const newReportingStream = ref('');

const addReportingStream = () => {
  showNewReportingStreamsInput.value = true;
};

const addAlias = () => {
  showNewAliasInput.value = true;
};

const saveAlias = async () => {
  saveAliasLoading.value = true;

  newAlias.value = newAlias.value.trim();

  const nameRegex = /^[a-zA-Z0-9-_() ]+$/;
  if (!nameRegex.test(newAlias.value)) {
    showToastMessage('error', 'Error', 'Alias can only contain alphanumeric characters, hyphen, underscore and parentheses');
    saveAliasLoading.value = false;
    return;
  }

  const createAliasResponse = await SchoolService.createAlias(router, store, selectedSchool.value.id, newAlias.value);
  if (createAliasResponse.status === 'success') {
    showToastMessage('success', 'School Updated', `Alias ${newAlias.value} has been saved for school ${selectedSchool.value.name}`);
    selectedSchool.value.aliases.push({
      id: createAliasResponse.id,
      alias: createAliasResponse.alias,
    });
    updateSchoolsTableWithoutReloading(selectedSchool.value, 'alias');
  } else {
    showToastMessage('error', 'Error', createAliasResponse.message);
  }

  saveAliasLoading.value = false;
  newAlias.value = '';
  showNewAliasInput.value = false;
};

const saveReportingStream = async () => {
  saveReportingStreamLoading.value = true;

  newReportingStream.value = newReportingStream.value.trim();

  const nameRegex = /^[a-zA-Z0-9-_() ]+$/;
  if (!nameRegex.test(newReportingStream.value)) {
    showToastMessage('error', 'Error', 'Reporting Stream name can only contain alphanumeric characters, hyphen, underscore and parentheses');
    saveReportingStreamLoading.value = false;
    return;
  }

  //check if reporting stream already exists from front data, reporting stream name should be unique inside one school
  if (selectedSchool.value.reporting_streams.find(stream => stream.name === newReportingStream.value)) {
    showToastMessage('error', 'Error', 'Reporting Stream Name is already taken');
    saveReportingStreamLoading.value = false;
    return;
  }

  const createReportingStreamResponse = await SchoolService.createReportingStream(router, store, selectedSchool.value.id, newReportingStream.value);
  if (createReportingStreamResponse.status === 'success') {
    showToastMessage('success', 'School Updated', `Reporting Stream ${newReportingStream.value} has been saved for school ${selectedSchool.value.name}`);
    const createdReportingStream = createReportingStreamResponse.reporting_stream;
    selectedSchool.value.reporting_streams.push({
      id: createdReportingStream.id,
      name: createdReportingStream.name,
    });

    updateSchoolsTableWithoutReloading(selectedSchool.value, 'reporting_stream');
  } else {
    //map message
    let errorMessage;
    if ("Reporting Stream already exists" === createReportingStreamResponse.message) {
      errorMessage = 'Reporting Stream Name already taken';
    } else {
      errorMessage = 'Failed to add reporting stream';
    }
    showToastMessage('error', 'Error', errorMessage);
    saveReportingStreamLoading.value = false;
  }

  newReportingStream.value = '';
  showNewReportingStreamsInput.value = false;
  saveReportingStreamLoading.value = false;

};

const cancelNewAlias = () => {
  showNewAliasInput.value = false;
  newAlias.value = '';
};

const cancelNewReportingStream = () => {
  showNewReportingStreamsInput.value = false;
  newReportingStream.value = '';
};

const newSchool = ref({
  name: '',
  aliases: [],
  reporting_streams: [],
  contracts: null,
  admin_ids: [],
});


const errorMessages = ref({
  schoolName: '',
  contractNumber: '',
  startDate: '',
  endDate: '',
});

watch(() => newSchool.value.name, (newValue) => {
  if (newValue !== '') {
    errorMessages.value.schoolName = '';
  }
});

const disableStartAndEndDates = ref(false);
watch(() => newSchool.value.contracts?.contract_number, (newValue) => {
  disableStartAndEndDates.value = newValue === '';
  errorMessages.value.contractNumber = '';
});

watch(() => newSchool.value.contracts?.term_start_date, (newValue) => {
  if (newValue !== '') {
    errorMessages.value.startDate = '';
  }
});

watch(() => newSchool.value.contracts?.term_end_date, (newValue) => {
  if (newValue !== '') {
    errorMessages.value.endDate = '';
  }
});

const newSchoolLoading = ref(false);

const addNewTempAlias = () => {
  newSchool.value.aliases.push('');
};


const deleteNewTempAlias = (index) => {
  newSchool.value.aliases.splice(index, 1);
};

const addNewTempReportingStream = () => {
  newSchool.value.reporting_streams.push('');
};

const deleteNewTempReportingStream = (index) => {
  newSchool.value.reporting_streams.splice(index, 1);
};

const addNewTempContract = () => {
  newSchool.value.contracts = {
    contract_number: '',
    term_start_date: '',
    term_end_date: '',
  };
};

// eslint-disable-next-line no-unused-vars
const deleteNewTempContract = () => {
  newSchool.value.contracts = null;
};

const cancelNewSchool = () => {
  newSchoolDrawer.value = false;
  showNewSchoolAliasPanel.value = false;
  showNewSchoolReportingStreamPanel.value = false;
  showNewSchoolContractsPanel.value = false;
  showNewSchoolNotifyAdminsPanel.value = false;

  newSchool.value = {
    name: '',
    aliases: [],
    reporting_streams: [],
    contracts: null,
    admin_ids: [],
  };
};

const addNewSchool = async () => {
  newSchoolLoading.value = true;

  if (newSchool.value.name === '') {
    errorMessages.value.schoolName = 'School name is required';
    newSchoolLoading.value = false;
    return;
  }

  if (schools.value.find(school => school.name.trim().toLowerCase() === newSchool.value?.name?.trim().toLowerCase())) {
    showToastMessage('error', 'Error', 'School Name is already taken');
    newSchoolLoading.value = false;
    return;
  }

  let contractNumberError = false;
  let startDateError = false;
  let endDateError = false;

  if (
      newSchool.value.contracts?.term_start_date instanceof Date
      || newSchool.value.contracts?.term_end_date instanceof Date
  ) {
    if (!newSchool.value.contracts?.contract_number?.trim?.()) {
      errorMessages.value.contractNumber = 'Contract Number is required when you have Start Date or End Date';
      contractNumberError = true;
    }
  }


  if (newSchool.value.contracts && newSchool.value.contracts.contract_number) {
    if (newSchool.value.contracts.term_start_date === '') {
      errorMessages.value.startDate = 'Start Date is required';
      startDateError = true;
    }

    let validationResult = SchoolService.validateStartEndDate(newSchool.value.contracts.term_start_date, newSchool.value.contracts.term_end_date);
    if (validationResult.status === 'error') {
      errorMessages.value.endDate = validationResult.message;
      endDateError = true;
    }
  }

  if (contractNumberError || startDateError || endDateError) {
    newSchoolLoading.value = false;
    return;
  }

  if (newSchool.value.reporting_streams.length === 1 && newSchool.value.reporting_streams[0] === '') {
    newSchool.value.reporting_streams = [];
  }

  if (newSchool.value.aliases.length === 1 && newSchool.value.aliases[0] === '') {
    newSchool.value.aliases = [];
  }

  if (newSchool.value.contracts && newSchool.value.contracts.contract_number === '') {
    newSchool.value.contracts = null;
  }

  //validate all names can only contains -_() and alphanumeric(of all languages)
  const nameRegex = /^[a-zA-Z0-9-_() ]+$/;
  // const nameRegex = /^[\p{L}0-9-_() ]+$/u;
  const validSchooName = ref(true);
  const validAliases = ref(true);
  const validReportingStreams = ref(true);
  const validContracts = ref(true);
  // if (!nameRegex.test(newSchool.value.name)) {
  //   showToastMessage('error', 'Error', 'School name can only contain alphanumeric characters, hyphen, underscore and parentheses');
  //   newSchoolLoading.value = false;
  //   validSchooName.value = false;
  // }

  for (let i = 0; i < newSchool.value.aliases.length; i++) {
    newSchool.value.aliases[i] = newSchool.value.aliases[i].trim();
    if (newSchool.value.aliases[i] === '') {
      newSchool.value.aliases.splice(i, 1);
      i--;
      continue;
    }

    if (!nameRegex.test(newSchool.value.aliases[i])) {
      showToastMessage('error', 'Error', 'Alias can only contain alphanumeric characters, hyphen, underscore and parentheses');
      newSchoolLoading.value = false;
      validAliases.value = false;
    }
  }

  for (let i = 0; i < newSchool.value.reporting_streams.length; i++) {
    newSchool.value.reporting_streams[i] = newSchool.value.reporting_streams[i].trim();
    if (newSchool.value.reporting_streams[i] === '') {
      newSchool.value.reporting_streams.splice(i, 1);
      i--;
      continue;
    }
    if (!nameRegex.test(newSchool.value.reporting_streams[i])) {
      showToastMessage('error', 'Error', 'Reporting Stream can only contain alphanumeric characters, hyphen, underscore and parentheses');
      newSchoolLoading.value = false;
      validReportingStreams.value = false;
    }
  }

  // reporting streams should be unique within this school
  const reportingStreamSet = new Set(newSchool.value.reporting_streams);
  if (reportingStreamSet.size !== newSchool.value.reporting_streams.length) {
    showToastMessage('error', 'Error', 'Reporting Stream Names should be unique per school');
    newSchoolLoading.value = false;
    validReportingStreams.value = false;
  }

  if (newSchool.value.contracts) {
    if (!nameRegex.test(newSchool.value.contracts.contract_number)) {
      showToastMessage('error', 'Error', 'Contract number can only contain alphanumeric characters, hyphen, underscore and parentheses');
      newSchoolLoading.value = false;
      validContracts.value = false;
    }
  }

  if (!validSchooName.value || !validAliases.value || !validReportingStreams.value || !validContracts.value) {
    return;
  }

  const newSchoolResponse = await SchoolService.addNewSchoolWithAssociatedData(router, store, newSchool.value.name, newSchool.value.reporting_streams, newSchool.value.aliases, newSchool.value.contracts, newSchool.value.admin_ids);
  if (newSchoolResponse.status === 'success') {
    showToastMessage('success', 'School Created', `School ${newSchool.value.name} has been created successfully`);
    showNewSchoolAliasPanel.value = false;
    showNewSchoolReportingStreamPanel.value = false;
    showNewSchoolContractsPanel.value = false;
    showNewSchoolNotifyAdminsPanel.value = false;
    listSchoolDetail();
    newSchool.value = {
      name: '',
      aliases: [],
      reporting_streams: [],
      contracts: null,
      admin_ids: [],
    };
    newSchoolDrawer.value = false;
  } else {
    showToastMessage('error', 'Error', newSchoolResponse.message);
    newSchoolDrawer.value = true;
  }

  newSchoolLoading.value = false;
};

const showNewSchoolAliasPanel = ref(false);
const showNewSchoolReportingStreamPanel = ref(false);
const showNewSchoolContractsPanel = ref(false);
const showNewSchoolNotifyAdminsPanel = ref(false);

const toggleNewSchoolAliasPanel = () => {
  showNewSchoolAliasPanel.value = !showNewSchoolAliasPanel.value;
  if (showNewSchoolAliasPanel.value && newSchool.value.aliases.length === 0) {
    addNewTempAlias();
  }
};

const toggleNewSchoolReportingStreamPanel = () => {
  showNewSchoolReportingStreamPanel.value = !showNewSchoolReportingStreamPanel.value;
  if (showNewSchoolReportingStreamPanel.value && newSchool.value.reporting_streams.length === 0) {
    addNewTempReportingStream();
  }
};

const toggleNewSchoolContractsPanel = () => {
  showNewSchoolContractsPanel.value = !showNewSchoolContractsPanel.value;
  if (showNewSchoolContractsPanel.value && !newSchool.value.contracts) {
    addNewTempContract();
  }
};

const toggleNewSchoolNotifyAdminsPanel = () => {
  showNewSchoolNotifyAdminsPanel.value = !showNewSchoolNotifyAdminsPanel.value;
};

</script>

<template>
  <div v-if="props.showSchoolsTab">
    <h1 class="text-3xl font-bold py-8 text-primaryBlue flex items-center">
      Schools Information
      <Button class="py-1 ml-8 flex items-center" @click="toggleNewSchoolDrawer">
        <i class="pi pi-plus mr-2"></i>
        <p>New School</p>
      </Button>
      <Drawer v-model:visible="newSchoolDrawer" position="right" class="w-[500px]">
        <template #header>
          <h1 class="text-2xl justify-end"></h1>
        </template>
        <h1 class="font-bold text-2xl mt-8 mb-6 px-4">New School</h1>
        <div class="flex flex-col gap-2 px-4">
          <label for="newSchoolName">School Name</label>
          <InputText id="newSchoolName" v-model="newSchool.name"/>
          <Message severity="error" v-if="errorMessages.schoolName"> {{ errorMessages.schoolName }}</Message>
          <div id="new-school-alias">
            <div id="new-school-alias-button-area"
                 class="relative w-full h-full">
              <Button
                  class="bg-transparent border-none pl-0 absolute transition-transform duration-500 ease-in-out"
                  :class="showNewSchoolAliasPanel ? 'translate-x-76 p-button-danger' : 'translate-x-0 '"
                  :icon="showNewSchoolAliasPanel ? 'pi pi-times' : 'pi pi-plus'"
                  iconPos="left"
                  :label="showNewSchoolAliasPanel ? 'Hide All Alias' : 'Add Alias'"
                  outlined
                  @click="toggleNewSchoolAliasPanel"/>
            </div>
            <Panel v-if="showNewSchoolAliasPanel">
              <template #header>
                <div class="panel-header ">
                  <div>Aliases</div>
                </div>
              </template>
              <div id="new-alias-input-field" class="w-full pl-4 flex flex-col items-center">
                <div v-for="(alias, index) in newSchool.aliases" :key="index"
                     class="w-full flex items-center justify-between my-2">
                  <InputText type="text" v-model="newSchool.aliases[index]" class="w-64"/>

                  <Button
                      icon="pi pi-trash"
                      class="p-button-danger border-none"
                      outlined
                      rounded
                      @click="deleteNewTempAlias(index)"
                  />
                </div>
              </div>
              <Button
                  class="bg-transparent border-none pl-0"
                  icon="pi pi-plus"
                  iconPos="left"
                  label="Add another Alias"
                  outlined
                  @click="addNewTempAlias"/>
            </Panel>
          </div>

          <div id="new-school-reporting-stream">
            <div id="new-school-reporting-stream-button-area"
                 class="relative w-full h-full">
              <Button
                  class="bg-transparent border-none pl-0 absolute transition-transform duration-500 ease-in-out"
                  :class="showNewSchoolReportingStreamPanel ? 'translate-x-52 p-button-danger' : 'translate-x-0 '"
                  :icon="showNewSchoolReportingStreamPanel ? 'pi pi-times' : 'pi pi-plus'"
                  iconPos="left"
                  :label="showNewSchoolReportingStreamPanel ? 'Hide All Reporting Stream' : 'Add Reporting Stream'"
                  outlined
                  @click="toggleNewSchoolReportingStreamPanel"/>
            </div>
            <Panel v-if="showNewSchoolReportingStreamPanel">
              <template #header>
                <div class="panel-header ">
                  <div>Reporting Streams</div>
                </div>
              </template>
              <div id="new-alias-input-field" class="w-full pl-4 flex flex-col items-center">
                <div v-for="(reporting_stream, index) in newSchool.reporting_streams" :key="index"
                     class="w-full flex items-center justify-between my-2">
                  <InputText type="text" v-model="newSchool.reporting_streams[index]" class="w-64"/>

                  <Button
                      icon="pi pi-trash"
                      class="p-button-danger border-none"
                      outlined
                      rounded
                      @click="deleteNewTempReportingStream(index)"
                  />
                </div>
              </div>
              <Button
                  class="bg-transparent border-none pl-0"
                  icon="pi pi-plus"
                  iconPos="left"
                  label="Add another Reporting Stream"
                  outlined
                  @click="addNewTempReportingStream"/>
            </Panel>
          </div>

          <div id="new-school-contracts">
            <div id="new-school-contracts-button-area"
                 class="relative w-full h-full">
              <Button
                  class="bg-transparent border-none pl-0 absolute transition-transform duration-500 ease-in-out"
                  :class="showNewSchoolContractsPanel ? 'translate-x-52 p-button-danger' : 'translate-x-0 '"
                  :icon="showNewSchoolContractsPanel ? 'pi pi-times' : 'pi pi-plus'"
                  iconPos="left"
                  :label="showNewSchoolContractsPanel ? 'Hide All Contract' : 'Add Contract'"
                  outlined
                  @click="toggleNewSchoolContractsPanel"/>
            </div>
            <Panel v-if="showNewSchoolContractsPanel">
              <template #header>
                <div class="panel-header ">
                  <div>Contracts</div>
                </div>
              </template>
              <div v-if="newSchool.contracts" id="new-contract-input-field" class="w-full pl-4 space-y-2">
                <div class="flex items-center justify-between w-full">
                  <InputText v-model="newSchool.contracts.contract_number" placeholder="Contract Number" class="w-64"/>
<!--                  <Button-->
<!--                      icon="pi pi-trash"-->
<!--                      class="p-button-danger border-none"-->
<!--                      outlined-->
<!--                      rounded-->
<!--                      @click="deleteNewTempContract"-->
<!--                  />-->
                </div>

                <Message severity="error" v-if="errorMessages.contractNumber" class="w-64"> {{errorMessages.contractNumber}} </Message>

                <div>
                  <DatePicker v-model="newSchool.contracts.term_start_date" class="w-64" placeholder="Start Date"
                              dateFormat="yy-mm-dd" :disabled="disableStartAndEndDates"/>
                  <Message severity="error" v-if="errorMessages.startDate"> {{ errorMessages.startDate }}</Message>
                </div>

                <div>
                  <DatePicker v-model="newSchool.contracts.term_end_date" class="w-64" placeholder="End Date"
                              dateFormat="yy-mm-dd" :disabled="disableStartAndEndDates" showButtonBar/>
                  <Message severity="error" v-if="errorMessages.endDate"> {{ errorMessages.endDate }}</Message>
                </div>
              </div>
              <!--              <Button-->
              <!--                  class="bg-transparent border-none pl-0"-->
              <!--                  icon="pi pi-plus"-->
              <!--                  iconPos="left"-->
              <!--                  label="Add Contract"-->
              <!--                  outlined-->
              <!--                  @click="addNewTempContract" />-->
            </Panel>
          </div>

          <!-- Notify Admins section - Only visible to Super Admin -->
          <div v-if="isSuperAdmin" id="new-school-notify-admins">
            <div id="new-school-notify-admins-button-area"
                 class="relative w-full h-full">
              <Button
                  class="bg-transparent border-none pl-0 absolute transition-transform duration-500 ease-in-out"
                  :class="showNewSchoolNotifyAdminsPanel ? 'translate-x-52 p-button-danger' : 'translate-x-0 '"
                  :icon="showNewSchoolNotifyAdminsPanel ? 'pi pi-times' : 'pi pi-plus'"
                  iconPos="left"
                  :label="showNewSchoolNotifyAdminsPanel ? 'Hide Notify Admins' : 'Add Notify Admins'"
                  outlined
                  @click="toggleNewSchoolNotifyAdminsPanel"/>
            </div>
            <Panel v-if="showNewSchoolNotifyAdminsPanel">
              <template #header>
                <div class="panel-header">
                  <div>Notify Admins</div>
                </div>
              </template>
              <MultiSelect
                  v-model="newSchool.admin_ids"
                  :options="availableAdmins"
                  optionLabel="email"
                  optionValue="id"
                  placeholder="Select admins to notify"
                  :loading="loadingAdminList"
                  display="chip"
                  filter
                  filterPlaceholder="Search admins"
                  class="w-full m-4"
              />
            </Panel>
          </div>

          <div id="create-school-button-area" class="my-8 flex justify-between items-center">
            <Button
                class="font-bold ml-24 px-6"
                label="Cancel"
                outlined
                @click="cancelNewSchool"/>

            <Button
                class="font-bold px-6"
                icon="pi pi-check"
                iconPos="left"
                label="Create School"
                :loading="newSchoolLoading"
                @click="addNewSchool"/>
          </div>
        </div>
      </Drawer>

    <Drawer v-model:visible="detailSchoolDrawer" position="right" class="w-[500px]">
      <template #header>
        <h1 class="text-2xl justify-end"></h1>
      </template>
        <h1 class="font-bold text-2xl mt-8 mb-6">{{ selectedSchool.name }}</h1>

        <Panel toggleable :collapsed="aliasesCollapsed" class="mb-6">
          <template #header>
            <div class="panel-header flex items-center justify-between">
              <div class="expand-button-container" @click="toggleAliasesPanel">
                <Button :icon=aliasesIcon class="p-button-text"/>
              </div>

              <div>Aliases</div>
            </div>
          </template>

          <template #toggleicon>
            <Tag severity="danger" :value="selectedSchool.aliasCount" class="bg-red-300 text-red-900"></Tag>
          </template>

          <div>
            <DataTable v-model:editingRows="aliasesEditingRows"
                       :value="selectedSchool.aliases"
                       editMode="row"
                       dataKey="id"
                       @row-edit-save="onRowEditSaveAlias"
                       id="aliasesTable"
            >

              <Column field="alias" class="border-none py-1 w-64">
                <template #editor="{ data, field }">
                  <InputText v-model="data[field]" fluid/>
                </template>
              </Column>

              <Column :rowEditor="true" bodyStyle="text-align:right" class="border-none pr-0 py-1"></Column>

              <template #footer>
                <div v-if="showNewAliasInput" id="new-alias-input-field" class="w-full pl-4 flex items-center justify-between">
                  <InputText type="text" v-model="newAlias" class="w-56"/>
                  <div class="ml-4 flex">
                    <Button
                        icon="pi pi-check"
                        class="p-button-success border-none"
                        outlined
                        rounded
                        :loading="saveAliasLoading"
                        @click="saveAlias"
                    />
                    <Button
                        icon="pi pi-trash"
                        class="p-button-danger border-none"
                        outlined
                        rounded
                        @click="cancelNewAlias"
                    />
                  </div>
                </div>

                <Button
                    class="bg-transparent border-none pl-0"
                    icon="pi pi-plus"
                    iconPos="left"
                    label="Add another Alias"
                    outlined
                    @click="addAlias"/>
              </template>
            </DataTable>
          </div>
        </Panel >

        <Panel toggleable :collapsed="reportingStreamsCollapsed" class="mb-6">
          <template #header>
            <div class="panel-header flex items-center justify-between">
              <div class="expand-button-container" @click="toggleReportingStreamsPanel">
                <Button :icon=reportingStreamsIcon class="p-button-text"/>
              </div>

              <div>Reporting Streams</div>
            </div>
          </template>

          <template #toggleicon>
            <Tag severity="danger" :value="selectedSchool.reportingStreamCount" class="bg-red-300 text-red-900"></Tag>
          </template>

          <div>
            <DataTable v-model:editingRows="reportingStreamsEditingRows"
                       :value="selectedSchool.reporting_streams"
                       editMode="row"
                       dataKey="id"
                       @row-edit-save="onRowEditSaveReportingStream"
                       id="reportingStreamsTable"
            >

              <Column field="name" class="border-none py-1 w-64">
                <template #editor="{ data, field }">
                  <InputText v-model="data[field]" fluid/>
                </template>
              </Column>

              <Column :rowEditor="true" bodyStyle="text-align:right" class="border-none pr-0 py-1"></Column>

              <template #footer>
                <div id="new-rs-input-field" class="w-full pl-4 flex items-center justify-between" v-if="showNewReportingStreamsInput">
                  <InputText type="text" v-model="newReportingStream" class="w-56"/>
                  <div class="ml-4 flex">
                    <Button
                        icon="pi pi-check"
                        class="p-button-success border-none"
                        outlined
                        rounded
                        :loading="saveReportingStreamLoading"
                        @click="saveReportingStream"
                    />
                    <Button
                        icon="pi pi-trash"
                        class="p-button-danger border-none"
                        outlined
                        rounded
                        @click="cancelNewReportingStream"
                    />
                  </div>
                </div>

                <Button
                    class="bg-transparent border-none pl-0"
                    icon="pi pi-plus"
                    iconPos="left"
                    label="Add another Reporting Stream"
                    outlined
                    @click="addReportingStream"/>
              </template>
            </DataTable>
          </div>
        </Panel>

        <Panel toggleable :collapsed="contractsCollapsed" class="mb-6">
          <template #header>
            <div class="panel-header flex items-center justify-between">
              <div class="expand-button-container" @click="toggleContractsPanel">
                <Button :icon=contractsIcon class="p-button-text"/>
              </div>

              <div>Contracts</div>
            </div>
          </template>

          <template #toggleicon>
            <Tag severity="danger" :value="selectedSchool.contractCount" class="bg-red-300 text-red-900"></Tag>
          </template>

          <div v-if="selectedSchool.contractCount > 0 || contractMode === 'edit'">
            <div class="flex justify-between items-center">
              <div class="flex-1">
                <div v-if="contractMode === 'display'" class="w-full pl-4">
                  {{ selectedSchool.contractNumber }}
                </div>
                <div v-else-if="contractMode === 'edit'" class="w-full pl-4">
                  <InputText v-model="selectedContract.contract_number" placeholder="Contract Number" class="w-64"
                             :disabled="selectedSchool.contractCount > 0"/>
                </div>
              </div>
              <div id="contract-right" class="flex space-x-2">
                <Button
                    v-if="contractMode === 'display'"
                    icon="pi pi-pencil"
                    class="border-none"
                    outlined
                    rounded
                    @click="contractModeChange('edit')"
                />
                <Button
                    v-else-if="contractMode === 'edit'"
                    icon="pi pi-times"
                    class="border-none"
                    outlined
                    rounded
                    @click="contractModeChange('display')"
                />
              </div>
            </div>

            <div v-if="contractMode === 'display'" class="w-full pl-4 mt-2">
              {{ selectedSchool.contractTermStartDate }}
            </div>
            <div v-else-if="contractMode === 'edit'" class="w-full pl-4 mt-2">
              <DatePicker v-model="selectedContract.term_start_date"
                          class="w-64"
                          dateFormat="yy-mm-dd"
                          placeholder="Start Date"/>
            </div>

            <div v-if="contractMode === 'display'" class="w-full mt-2 pl-4">
              {{ selectedSchool.contractTermEndDate }}
            </div>
            <div v-else-if="contractMode === 'edit'" class="w-full mt-2 pl-4">
              <DatePicker v-model="selectedContract.term_end_date"
                          class="w-64"
                          dateFormat="yy-mm-dd"
                          placeholder="End Date"
                          showButtonBar/>
            </div>

            <div v-if="contractMode === 'edit'">
              <Button icon="pi pi-check"
                      class="border-none mt-2 ml-4"
                      label="Save Contract"
                      @click="saveContract"/>
            </div>
          </div>
          <div v-else>
            <Button
                class="bg-transparent border-none pl-0"
                icon="pi pi-plus"
                iconPos="left"
                label="Add new Contract"
                outlined
                @click="addContract"/>
          </div>

        </Panel>

        <!-- Notify Admins Panel - Only visible to Super Admin -->
        <Panel v-if="isSuperAdmin" toggleable :collapsed="notifyAdminsCollapsed" class="mb-6">
          <template #header>
            <div class="panel-header flex items-center justify-between">
              <div class="expand-button-container" @click="toggleNotifyAdminsPanel">
                <Button :icon=notifyAdminsIcon class="p-button-text"/>
              </div>

              <div>Notify Admins</div>
            </div>
          </template>

          <template #toggleicon>
            <Tag severity="danger" :value="selectedSchool.notifyAdmins?.length || 0" class="bg-red-300 text-red-900"></Tag>
          </template>

          <div class="multiselect-notify-container w-full pl-4 py-2">
            <MultiSelect
                :modelValue="selectedSchool.notifyAdmins"
                :options="availableAdmins"
                optionLabel="email"
                optionValue="id"
                placeholder="Select admins to notify"
                :loading="selectedSchool.loadingNotifyAdmins || loadingAdminList"
                display="chip"
                filter
                filterPlaceholder="Search admins"
                class="w-full"
                @update:modelValue="(value) => updateSchoolAdmins(selectedSchool, value)"
            />
          </div>
        </Panel>

        <Panel toggleable :collapsed="logsCollapsed" class="mb-6">
          <template #header>
            <div class="panel-header flex items-center justify-between">
              <div class="expand-button-container" @click="toggleLogsPanel">
                <Button :icon=logsIcon class="p-button-text"/>
              </div>

              <div>Activity Logs</div>
            </div>
          </template>

          <template #toggleicon>
            <span class="hidden"></span>
          </template>

          <ActivityLog :events="schoolActivityLogs"></ActivityLog>
          <Button v-if="canLoadMoreLogs" label="Load More" class="p-button-text"  @click="loadMoreLogs" />
        </Panel>

      </Drawer>
    </h1>

    <div class="card">
      <DataTable
          id="schoolsTable"
          v-model:filters="filters"
          :value="schools"
          removableSort
          rowHover
          stripedRows
          highlightOnSelect
          :rows="perPage"
          dataKey="id"
          :loading="table_loading"
          paginator
          lazy
          :totalRecords="totalRecords"
          @page="onPageChange"
          @sort="onSort"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          :rowsPerPageOptions="[10, 20, 30]"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} schools"
      >
        <template #header>
          <div class="flex justify-start w-[450px]">
            <IconField class="w-full">
              <InputIcon>
                <i class="pi pi-search"/>
              </InputIcon>
              <InputText
                  class="w-full"
                  v-model="filters['global'].value"
                  placeholder="Search"
              />
            </IconField>
          </div>
        </template>
        <template #empty> No schools found.</template>
        <template #loading> Loading schools data. Please wait.</template>

<!--        <Column field="id" header="Id" :sortable="true" style="min-width: 4rem">-->
<!--          <template #body="{ data }">-->
<!--            {{ data.id }}-->
<!--          </template>-->
<!--        </Column>-->

        <Column field="name" header="Name" :sortable="true" style="min-width: 12rem">
          <template #body="{ data }">
            {{ data.name }}
          </template>
        </Column>

        <Column
            field="firstAlias"
            header="Aliases"
            style="min-width: 12rem"
        >
          <template #body="{ data }">
            <Tag
                v-if="data.aliasCount > 0"
                severity="secondary"
                :value="data.firstAlias"
                class="bg-gray-300 text-black mr-0.5"
            ></Tag>
            {{ data.aliasCount > 1 ? `+${data.aliasCount - 1}` : '' }}
          </template>
        </Column>


        <Column
            field="firstReportingStream"
            header="Reporting Streams"
            style="min-width: 12rem"
        >
          <template #body="{ data }">
            <Tag
                v-if="data.reportingStreamCount > 0"
                severity="secondary"
                :value="data.firstReportingStream"
                class="bg-gray-300 text-black mr-0.5"
            ></Tag>
            {{ data.reportingStreamCount > 1 ? `+${data.reportingStreamCount - 1}` : '' }}
          </template>
        </Column>

        <Column
            field="activeUsersCount"
            header="Active Students"
            :sortable="true"
            style="min-width: 8rem"
        >
          <template #body="{ data }">
            {{ data.activeUsersCount }}
          </template>
        </Column>

        <Column
            field="contractTermStartDateSort"
            header="Contracts"
            :sortable="true"
            style="min-width: 12rem"
        >
          <template #body="{ data }">
            <div class="flex flex-col space-y-1">
              <div>{{ data?.contracts?.[0]?.contract_number || '' }}</div>
              <div class="text-sm text-gray-600">{{ data?.contractTermStartDate || '' }}</div>
              <div class="text-sm text-gray-600">{{ data?.contractTermEndDate || '' }}</div>
            </div>
          </template>
        </Column>

        <Column
            field="notifyAdmins"
            header="Notify"
            style="min-width: 14rem"
        >
          <template #body="{ data }">
            <!-- Read-only display for everyone -->
            <div class="flex flex-wrap gap-2">
              <Tag
                  v-for="adminId in data.notifyAdmins"
                  :key="adminId"
                  :value="availableAdmins.find(a => a.id === adminId)?.email || adminId"
                  severity="secondary"
                  class="bg-gray-300 text-black"
              />
            </div>
          </template>
        </Column>

        <Column class="w-24 text-end!">
          <template #body="{ data }">
            <Button icon="pi pi-eye" @click="selectSchool(data)" severity="secondary"
                    class="bg-transparent border-none"></Button>
          </template>
        </Column>
      </DataTable>

    </div>
  </div>
</template>

<style>
#aliasesTable .p-datatable-table-container .p-datatable-table .p-datatable-thead {
  display: none;
}

#aliasesTable .p-datatable-footer {
  background-color: transparent;
  border: none;
  padding-left: 0;
  padding-right: 0;
}

#reportingStreamsTable .p-datatable-table-container .p-datatable-table .p-datatable-thead {
  display: none;
}

#reportingStreamsTable .p-datatable-footer {
  background-color: transparent;
  border: none;
  padding-left: 0;
  padding-right: 0;
}

#schoolsTable .p-paginator {
  justify-content: flex-end;
  padding-right: 30px;
}
</style>